import React from 'react'
import { connect } from 'react-redux'
import { bindActionCreators, Dispatch, Action } from 'redux'
import { RouterProvider } from 'react-router-dom'
import Cookies from 'universal-cookie'
import getParamValueFromUrl from '@Utils/GetParamValueFromUrl'
import ChecksubLogo from '@Images/cs_logo_white.png'
import ChecksubLogoBlack from '@Images/cs_logo_black.png'
import posthog from 'posthog-js'

import router from './Router'

import { subscribeToAccount, unsubscribeFromAccount } from '@SiteModules/Account/cable'

import PageLoader from '@EditorComponents/PageLoader'
import ExportationsProgress from '@EditorComponents/ExportationsProgress'
import Alert from '@SiteComponents/Alert'
import RenewSubscriptionAlert from '@SiteComponents/RenewSubscriptionAlert'

import {
  IAlert,
  ISetLastViewedAlert,
} from '@SiteModules/User/types'

import {
  IFollowedExportation,
} from '@EditorModules/Exportation/types'

import {
  IFetchWorkspacesPayload,
  ISetCurrentAccountPayload,
  IAccount,
} from '@SiteModules/Account/types'

import {
  ISetCurrentWorkspacePayload,
} from '@SiteModules/Workspace/types'

import {
  getUserInfo,
  setLastViewedAlert,
  setUseLightTheme,
} from '@SiteModules/User/actions'

import {
  getFollowedExportations,
} from '@SiteModules/Exportation/actions'

import {
  resetCurrentAccount,
  fetchWorkspaces,
  setCurrentAccount,
} from '@SiteModules/Account/actions'

import {
  resetAccountUsers,
} from '@SiteModules/AccountUser/actions'

import {
  resetCurrentWorkspace,
  resetWorkspaces,
  setCurrentWorkspace,
} from '@SiteModules/Workspace/actions'

import {
  getAccountIds,
  getCurrentAccount,
  getUserIsAccountAdmin,
  getCurrentAccountHasCurrentSubscription,
} from '@SiteModules/Account/selectors'

import {
  getWorkspaceIds,
} from '@SiteModules/Workspace/selectors'

import {
  getLastUsedAccountId,
  getLastUsedWorkspaceId,
  getUseLightTheme,
  getHasConfirmedEmail,
  getUserId,
} from '@SiteModules/User/selectors'

import { IApplicationState } from './rootReducer'

interface ChecksubAppProps {
  readonly shouldGetUserInfo: boolean
  readonly relevantAlert: IAlert | null
  readonly accountIds: number[]
  readonly currentAccountId: number
  readonly workspaceIds: number[]
  readonly currentWorkspaceId: number
  readonly currentAccount: IAccount | undefined
  readonly userName: string
  readonly userEmail: string
  readonly userId: number
  readonly followedExportations: IFollowedExportation[]
  readonly lastUsedAccountIdOfUser: number
  readonly lastUsedWorkspaceIdOfUser: number
  readonly useLightTheme: boolean
  readonly userIsAccountAdmin: boolean
  readonly currentAccountHasCurrentSubscription: boolean
  readonly userHasConfirmedEmail: boolean
  readonly isPaymentProcessing: boolean
  getUserInfo(): void
  setLastViewedAlert(payload: ISetLastViewedAlert): void
  getFollowedExportations(): void
  resetCurrentAccount(): void
  resetCurrentWorkspace(): void
  fetchWorkspaces(payload: IFetchWorkspacesPayload): void
  resetWorkspaces(): void
  setCurrentAccount(payload: ISetCurrentAccountPayload): void
  setCurrentWorkspace(payload: ISetCurrentWorkspacePayload): void
  resetAccountUsers(): void
  setUseLightTheme(): void
}

interface ChecksubAppState {
  readonly shouldWait: boolean
  readonly intervalIdForFollowedExportations: number
}

const AUTO_UPDATE_TIME_EXPORTATIONS= 10000 // 10 seconds

class ChecksubApp extends React.Component<ChecksubAppProps, ChecksubAppState> {
  constructor(props: ChecksubAppProps) {
    super(props)
    this.state = {
      shouldWait: true,
      intervalIdForFollowedExportations: -1,
    }
  }

  unsubscribe: (() => void) | null = null

  cleanUpUrl = (urlParam: string) => {
    const url = new URL(window.location.href)
    url.searchParams.delete(urlParam)
    window.history.replaceState({}, '', url)
  }

  componentDidMount() {
    const { shouldGetUserInfo } = this.props

    setTimeout(() => {
      this.setState({ shouldWait: false })
      if (shouldGetUserInfo) { this.props.getUserInfo() }
    }, 0)

    setTimeout(() => {
      this.updateLogoVisibility()
    }, 0)

    this.unsubscribe = router.subscribe(() => {
      // Use requestAnimationFrame to update logo visibility in sync with the browser's paint cycle.
      // This ensures DOM is up to date when this code runs.
      requestAnimationFrame(() => {
        this.updateLogoVisibility()
      })
    })

    const cookies = new Cookies()
    const lightThemeCookieValue = cookies.get('useLightTheme')
    const shouldUseLightTheme = typeof(lightThemeCookieValue) === 'boolean' && lightThemeCookieValue
    if (shouldUseLightTheme) {
      this.props.setUseLightTheme()
    }

    this.updateTheme(shouldUseLightTheme)

    window.addEventListener('resize', this.updateLogoVisibility)

    if (this.props.isPaymentProcessing) {
      subscribeToAccount({
        accountId: this.props.currentAccountId,
        onSubscriptionUpdated: this.handleSubscriptionUpdated,
      })
    }
  }

  componentDidUpdate(prevProps: ChecksubAppProps) {
    if (this.props.shouldGetUserInfo) {
      this.props.getUserInfo()
    }

    setTimeout(() => {
      this.updateLogoVisibility()
    }, 0)

    if (prevProps.useLightTheme !== this.props.useLightTheme) {
      this.updateTheme(this.props.useLightTheme)
    }

    this.setTimerForUpdatingFollowedExportations()

    if (prevProps.accountIds !== this.props.accountIds && !this.props.accountIds.includes(this.props.currentAccountId)) {
      const accountIdFromUrl = getParamValueFromUrl(window.location.href, 'accountId')
      const targetAccountId = accountIdFromUrl ? parseInt(accountIdFromUrl, 10) : -1

      if (this.props.accountIds.includes(targetAccountId)) {
        this.props.setCurrentAccount({ accountId: targetAccountId })
      } else if (this.props.accountIds.includes(this.props.lastUsedAccountIdOfUser)) {
        this.props.setCurrentAccount({ accountId: this.props.lastUsedAccountIdOfUser })
      } else {
        this.props.resetCurrentAccount()
      }

      this.cleanUpUrl('accountId')
    }

    if (prevProps.currentAccountId !== this.props.currentAccountId) {
      if (this.props.currentAccountId >= 0) {
        this.props.fetchWorkspaces({ accountId: this.props.currentAccountId })
      } else {
        this.props.resetWorkspaces()
      }

      this.props.resetAccountUsers()
    }

    if (prevProps.workspaceIds !== this.props.workspaceIds && !this.props.workspaceIds.includes(this.props.currentWorkspaceId)) {
      const workspaceIdFromUrl = getParamValueFromUrl(window.location.href, 'workspaceId')
      const targetWorkspaceId = workspaceIdFromUrl ? parseInt(workspaceIdFromUrl, 10) : -1
      const lastUsedWorkspaceId = this.props.currentAccount?.lastUsedWorkspaceId || this.props.lastUsedWorkspaceIdOfUser

      if (this.props.workspaceIds.includes(targetWorkspaceId)) {
        this.props.setCurrentWorkspace({ workspaceId: targetWorkspaceId })
      } else if (typeof(lastUsedWorkspaceId) === 'number' && this.props.workspaceIds.includes(lastUsedWorkspaceId)) {
        this.props.setCurrentWorkspace({ workspaceId: lastUsedWorkspaceId })
      } else {
        this.props.resetCurrentWorkspace()
      }

      this.cleanUpUrl('workspaceId')
    }

    if (!window.Checksub.devMode) {
      if (prevProps.userEmail !== this.props.userEmail && this.props.userEmail) {
        posthog.identify(this.props.userEmail)
      }
    }

    if (!prevProps.isPaymentProcessing && this.props.isPaymentProcessing) {
      subscribeToAccount({
        accountId: this.props.currentAccountId,
        onSubscriptionUpdated: this.handleSubscriptionUpdated,
      })
    }
  }

  componentWillUnmount() {
    clearInterval(this.state.intervalIdForFollowedExportations)
    if (this.unsubscribe) {
      this.unsubscribe()
    }

    unsubscribeFromAccount()
    window.removeEventListener('resize', this.updateLogoVisibility)
  }

  handleSubscriptionUpdated = () => {
    unsubscribeFromAccount()
    this.props.getUserInfo()
  }

  updateLogoVisibility = () => {
    const logo = document.getElementById('persistent-logo')
    const hasSidebar = document.querySelector('.Sidebar') !== null

    if (logo) {
      logo.style.opacity = hasSidebar ? '1' : '0'
    }
  }

  updateTheme(useBrightTheme: boolean) {
    const root = document.documentElement
    if (useBrightTheme) {
      root.setAttribute('data-theme', 'light')
    } else {
      root.removeAttribute('data-theme')
    }
  }

  setTimerForUpdatingFollowedExportations = () => {
    const { intervalIdForFollowedExportations } = this.state
    const shouldUpdateOngoingExportations = this.props.followedExportations.length > 0
    const isTimerPresent = intervalIdForFollowedExportations !== -1
    const shouldSetupTimerForFetchingUpdates = shouldUpdateOngoingExportations && !isTimerPresent
    const shouldRemoveInterval = !shouldUpdateOngoingExportations && isTimerPresent

    if (shouldSetupTimerForFetchingUpdates) {
      const newTimerId = window.setInterval(this.props.getFollowedExportations, AUTO_UPDATE_TIME_EXPORTATIONS)
      this.setState({ intervalIdForFollowedExportations: newTimerId })
    } else if (shouldRemoveInterval) {
      clearInterval(this.state.intervalIdForFollowedExportations)
      this.setState({ intervalIdForFollowedExportations: -1 })
    }
  }

  closeAlert = () => {
    if (!this.props.relevantAlert?.id) { return }

    this.props.setLastViewedAlert({ alertId: this.props.relevantAlert.id })
  }

  renderChecksubLogo = () => { // Rendered here instead of inside the sidebar to prevent flickering when the page changes
    return (
      <img
        src={this.props.useLightTheme ? ChecksubLogoBlack : ChecksubLogo}
        className="ChecksubApp__logo"
        id="persistent-logo"
        style={{
          height: '26px',
          position: 'absolute',
          top: '48px',
          left: '38px',
          zIndex: '100',
          transition: 'opacity 0.2s ease'
        }}
      />
    )
  }

  renderExportationsProgress = () => {
    const {
      userName,
      followedExportations
    } = this.props

    return (
      <ExportationsProgress
        userName={userName}
        exportations={followedExportations}
      />
    )
  }

  render() {
    const {
      relevantAlert,
      userIsAccountAdmin,
      followedExportations,
      currentAccountHasCurrentSubscription,
      userHasConfirmedEmail,
      userId,
    } = this.props

    const {
      message,
      linkLabel,
      linkUrl,
    } = relevantAlert || {}

    const shouldRenderExportationsProgress = followedExportations.length > 0
    const shouldRenderRenewSubscriptionAlert = userId >= 0 && userHasConfirmedEmail && !currentAccountHasCurrentSubscription
    const excludedAlertPaths = ['/onboarding']
    const pathname = router.state.location.pathname
    const shouldHideAlert = excludedAlertPaths.some((path) => pathname.startsWith(path)) || getParamValueFromUrl(window.location.href, 'alert') === 'false'

    return(
      <div id="site-container">
        {this.state.shouldWait ? (
          <PageLoader/>
        ) : (
          <React.Fragment>
            {shouldRenderRenewSubscriptionAlert && (
              <RenewSubscriptionAlert
                userCanManageSubscription={userIsAccountAdmin}
                tooltipDelay={window.Checksub.tooltipDelay}
              />
            )}
            {message && !shouldHideAlert && (
              <Alert
                message={message}
                linkLabel={linkLabel}
                linkUrl={linkUrl}
                close={this.closeAlert}
                marginTop={shouldRenderRenewSubscriptionAlert ? 120 : undefined} // A number larger than the total height of RenewSubscriptionAlert
              />
            )}
            {this.renderChecksubLogo()}
            {shouldRenderExportationsProgress && this.renderExportationsProgress()}
            <RouterProvider router={router} />
          </React.Fragment>
        )}
      </div>
    )
  }
}

function mapStateToProps(state: IApplicationState) {
  const { user, exportations, accounts, workspaces } = state
  const isAuthorized = window.Checksub.cookies.get('authorized')

  return {
    shouldGetUserInfo: isAuthorized && user.user.id === -1,
    relevantAlert: user.user.attributes.relevantAlert,
    accountIds: getAccountIds(state),
    currentAccountId: accounts.currentAccountId,
    workspaceIds: getWorkspaceIds(state),
    currentWorkspaceId: workspaces.currentWorkspaceId,
    currentAccount: getCurrentAccount(state),
    userName: user.user.attributes.name || '',
    userEmail: user.user.attributes.email,
    followedExportations: exportations.followedExportations,
    lastUsedAccountIdOfUser: getLastUsedAccountId(state),
    lastUsedWorkspaceIdOfUser: getLastUsedWorkspaceId(state),
    useLightTheme: getUseLightTheme(state),
    userIsAccountAdmin: getUserIsAccountAdmin(state),
    currentAccountHasCurrentSubscription: getCurrentAccountHasCurrentSubscription(state),
    userHasConfirmedEmail: getHasConfirmedEmail(state),
    userId: getUserId(state),
    isPaymentProcessing: !!getCurrentAccount(state)?.subscription?.attributes.isPaymentProcessing,
  }
}

function mapDispatchToProps(dispatch: Dispatch<Action>) {
  return bindActionCreators({
    getUserInfo,
    setLastViewedAlert,
    getFollowedExportations,
    resetCurrentAccount,
    resetCurrentWorkspace,
    fetchWorkspaces,
    resetWorkspaces,
    setCurrentAccount,
    setCurrentWorkspace,
    resetAccountUsers,
    setUseLightTheme,
  }, dispatch)
}

export default connect(mapStateToProps, mapDispatchToProps)(ChecksubApp)
